import { debounce } from "Helper/functionHelper/decorators";
import { Component, ComponentArgs } from "@kluntje/core";
import { addClass, removeClass } from "@kluntje/js-utils/lib/dom-helpers";
import { scrollLock } from "Helper/domHelper";
import { fetchJSON } from "Helper/apiHelper";
import { HIDDEN, ACTIVE } from "Helper/cssClasses";
import { getCurrentMQ } from "Helper/domHelper";

export class FRAAutocomplete extends Component {
  constructor({ ui = {}, events = [], initialStates = {}, reactions = {} }: ComponentArgs = {}) {
    super({
      ui: {
        input: ".fra-js-autocomplete__input :-one",
        staticLinks: ".fra-js-autocomplete__static :-one",
        autocompleteList: ".fra-js-autocomplete__list :-one",
        resetBtn: ".fra-js-search__reset :-one",
        ...ui,
      },
      initialStates: {
        loaded: false,
        focused: false,
        hasStatic: false,
        ...initialStates,
      },
      events: [
        {
          event: "focus",
          target: "input",
          handler: "handleInputFocus",
        },
        {
          event: "blur",
          target: "input",
          handler: "handleInputBlur",
        },
        {
          event: "keyup",
          target: "input",
          handler: "handleSearchInput",
        },
        {
          event: "change",
          target: "input",
          handler: "handleSearchInput",
        },
        {
          event: "click",
          target: "autocompleteList",
          handler: "preventBlur",
        },
        {
          event: "click",
          target: "resetBtn",
          handler: "resetInput",
        },
        ...events,
      ],
    });

    Object.assign(this.uiDefinitions, ui);
    Object.assign(this.initialStates, initialStates);
    Object.assign(this.reactions, reactions);

    this.mergeEvents(events);
  }

  static INPUT_MIN_LENGTH = 3;

  /**
   * Returns provided target-url from custom element
   * @returns {string}
   */
  get targetUrl(): string {
    return this.getAttribute("data-target-url") || "";
  }

  /**
   * Returns provided api-url from custom element
   * @returns {string}
   */
  get apiUrl(): string {
    return this.getAttribute("data-api-url") || "";
  }

  get preserveListBlurAttribute() {
    return this.hasAttribute("preserve-list-blur");
  }

  renderSuggestions(suggestions: any) {
    const autocompleteItems = suggestions.reduce(
      // @ts-ignore
      (result, suggestion: { keyword: string; url: string; target: string }) => {
        result += `<li class="fra-e-autocomplete__list-item">
          <a class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right"
            href="${this.targetUrl}${suggestion.url.replaceAll("&", "%26").replace("%26", "&")}"
            target="_${suggestion.target ? suggestion.target : "self"}"
          >
            <span class="fra-e-link__label">${suggestion.keyword}</span>
            <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow-right"></fra-e-icon>
          </a>
        </li>`;

        return result;
      },
      "",
    );

    if (this.state.loaded) {
      this.ui.autocompleteList.innerHTML = autocompleteItems;
      this.showAutocompleteList();
    }
  }

  /**
   * Fetch data from API and return response
   * @param {string} keyword
   */
  async fetchSuggestions(keyword: string) {
    await fetchJSON(this.generateRequest(keyword))
      .then(response => {
        this.setState({ loaded: true });
        this.renderSuggestions(response.data);
      })
      .catch(error => {
        console.warn(`An error occurred while loading the component: ${error}`);
        return "";
      });
  }

  /**
   * Generate request to fetch data from
   * @param {string} keyword
   * @returns {string}
   */
  generateRequest(keyword: string): string {
    const keywordUrl = `?_charset_=UTF-8&q=${keyword}`;
    return `${this.apiUrl}${keywordUrl}`;
  }

  /**
   * handler for focus-actions
   */
  handleInputFocus(): void {
    this.setState({ hasStatic: this.ui.staticLinks !== null });
    if (this.ui.input.value.length > 0) {
      this.showResetBtn();
    }
    this.setState({ focused: false });
    addClass(this, ACTIVE);
  }

  /**
   * Hide suggestions and remove focus from input
   */
  collapse(): void {
    if (getCurrentMQ() === "MQ1" || getCurrentMQ() === "MQ2") {
      scrollLock.pageBodyUnlock();
    }
    this.showStaticLinks();
    this.hideAutocompleteList();
    this.setState({ focused: false });
    removeClass(this, ACTIVE);
  }

  /**
   * reset input to default state
   */
  resetInput(): void {
    this.ui.input.value = "";
    this.hideResetBtn();
    this.collapse();
  }

  @debounce(200)
  /**
   * handler for blur-actions
   */
  handleInputBlur(): void {
    if (this.state.focused) {
      return;
    }

    if (!this.preserveListBlurAttribute) {
      this.hideResetBtn();
      this.collapse();
    }

    // prevent to close the list on blur when the input has values and the list is shown
    if (this.preserveListBlurAttribute && !this.state.loaded) {
      this.hideResetBtn();
      this.collapse();
    }
  }

  /**
   * prevent blur from being fired
   */
  preventBlur(): void {
    this.setState({ focused: true });
  }

  /**
   * add HIDDEN to autocomplete list
   */
  showAutocompleteList(): void {
    removeClass(this.ui.autocompleteList, HIDDEN);
  }

  /**
   * remove HIDDEN from autocomplete list
   */
  hideAutocompleteList(): void {
    addClass(this.ui.autocompleteList, HIDDEN);
  }

  /**
   * add HIDDEN to static links
   */
  showStaticLinks(): void {
    if (this.state.hasStatic) {
      removeClass(this.ui.staticLinks, HIDDEN);
    }
  }

  /**
   * remove HIDDEN from static links
   */
  hideStaticLinks(): void {
    if (this.state.hasStatic) {
      addClass(this.ui.staticLinks, HIDDEN);
    }
  }

  /**
   * show/hide reset button in input
   */
  showResetBtn(): void {
    removeClass(this.ui.resetBtn, HIDDEN);
  }
  hideResetBtn(): void {
    addClass(this.ui.resetBtn, HIDDEN);
  }

  @debounce(300)
  /**
   * handler for input-actions
   * @param {any} e
   */
  handleSearchInput(e: any): void {
    const inputValue = e.target.value;
    const inputLength = inputValue.length;

    // prevent render suggestions when keyboard arrows are pressed
    if ([37, 38, 39, 40].indexOf(e.keyCode) !== -1) return;
    if (e.keyCode === 13) {
      // return detected: submit form
      this.collapse();
      return;
    }

    if (inputLength <= 2) {
      this.showStaticLinks();
      this.hideResetBtn();
    } else {
      this.hideStaticLinks();
      this.showResetBtn();
    }

    if (inputLength >= FRAAutocomplete.INPUT_MIN_LENGTH) {
      this.setState({ loaded: false });
      this.fetchSuggestions(inputValue);
      window.dataLayer?.push({
        event: "gaEvent",
        eventCategory: "Autocomplete Search Input",
        eventAction: "input",
        eventLabel: inputValue,
      });
    } else {
      this.hideAutocompleteList();
    }
  }
}

customElements.define("fra-e-autocomplete", FRAAutocomplete);

export default FRAAutocomplete;
